(function (global) {
  "use strict";

  var DEFAULT_DIGITAL_TIERS = [
    { min: 25, price: 13.50 },
    { min: 48, price: 13.00 },
    { min: 72, price: 12.50 },
    { min: 144, price: 12.00 },
    { min: 200, price: 11.50 },
    { min: 288, price: 11.00 },
    { min: 500, price: 10.50 }
  ];

  var SCREEN_PUBLIC_LOCATION_RATES = {
    72: [0, 2.75, 3.25, 3.75, 4.25, 4.50, 5.00, 5.75, 6.25, 6.75, 7.50],
    144: [0, 2.00, 2.75, 3.00, 3.50, 4.00, 4.50, 5.00, 5.50, 6.00, 6.50],
    288: [0, 1.75, 2.00, 2.50, 2.75, 3.00, 3.50, 4.00, 4.25, 5.00, 5.50],
    600: [0, 1.25, 1.75, 2.00, 2.25, 2.50, 2.75, 3.25, 3.50, 4.00, 4.25],
    1000: [0, 1.25, 1.50, 1.75, 2.00, 2.25, 2.50, 2.75, 3.00, 3.25, 3.75],
    3000: [0, 1.25, 1.25, 1.50, 1.75, 2.00, 2.25, 2.50, 2.75, 3.25, 3.50],
    5000: [0, 1.00, 1.25, 1.50, 1.75, 2.00, 2.25, 2.50, 2.75, 3.00, 3.50]
  };

  var SCREEN_BREAKS = [72, 144, 288, 600, 1000, 3000, 5000];
  var SCREEN_BASE_GARMENT = 7.75;
  var SCREEN_PLACEMENT_ADDER = { front: 0, back: 0, chest: 0, sleeve: 0.50 };

  function parseDigitalTiers(value) {
    if (!value) return DEFAULT_DIGITAL_TIERS.slice();
    var parsed = value.split(",").map(function (entry) {
      var parts = entry.split(":");
      return { min: Number(parts[0]), price: Number(parts[1]) };
    }).filter(function (tier) {
      return Number.isFinite(tier.min) && Number.isFinite(tier.price);
    }).sort(function (a, b) {
      return a.min - b.min;
    });
    return parsed.length ? parsed : DEFAULT_DIGITAL_TIERS.slice();
  }

  function getDigitalTier(tiers, quantity) {
    var current = null;
    tiers.forEach(function (tier) {
      if (quantity >= tier.min) current = tier;
    });
    return current;
  }

  function getNextDigitalTier(tiers, quantity) {
    return tiers.find(function (tier) {
      return tier.min > quantity;
    }) || null;
  }

  function getScreenBreak(quantity) {
    var current = SCREEN_BREAKS[0];
    SCREEN_BREAKS.forEach(function (tier) {
      if (quantity >= tier) current = tier;
    });
    return current;
  }

  function digitalEstimate(config) {
    var tiers = config.tiers || DEFAULT_DIGITAL_TIERS;
    var minimum = Number(config.minimum || 25);
    var groupMinimum = Number(config.groupMinimum || 12);
    var buyerPackagePrice = Number(config.buyerPackagePrice || 1);
    var buyerPackages = Math.max(0, Number(config.buyerPackages) || 0);
    var designs = config.designs || [];
    var totalQuantity = designs.reduce(function (sum, design) {
      return sum + Math.max(0, Number(design.quantity) || 0);
    }, 0);
    var tier = getDigitalTier(tiers, totalQuantity);
    var nextTier = getNextDigitalTier(tiers, totalQuantity);
    var reviewReasons = [];

    designs.forEach(function (design, index) {
      var quantity = Math.max(0, Number(design.quantity) || 0);
      var label = design.name || "Design " + (index + 1);
      if (quantity > 0 && quantity < groupMinimum) {
        reviewReasons.push(label + " needs at least " + groupMinimum + " pieces for automatic pricing.");
      }
      if (design.customReview) {
        reviewReasons.push(label + " uses a print layout that needs custom review.");
      }
    });

    if (buyerPackages > totalQuantity) {
      reviewReasons.push("Buyer-package count cannot be greater than the garment quantity.");
    }

    if (!tier || totalQuantity < minimum) {
      return {
        available: false,
        requiresReview: false,
        totalQuantity: totalQuantity,
        tier: null,
        nextTier: nextTier,
        reviewReasons: reviewReasons,
        lines: [],
        pricingSubtotal: 0,
        buyerPackages: buyerPackages,
        fulfillmentTotal: 0,
        total: 0,
        average: 0
      };
    }

    if (reviewReasons.length) {
      return {
        available: false,
        requiresReview: true,
        totalQuantity: totalQuantity,
        tier: tier,
        nextTier: nextTier,
        reviewReasons: reviewReasons,
        lines: [],
        pricingSubtotal: 0,
        buyerPackages: buyerPackages,
        fulfillmentTotal: 0,
        total: 0,
        average: 0
      };
    }

    var pricingSubtotal = 0;
    var lines = designs.map(function (design) {
      var quantity = Math.max(0, Number(design.quantity) || 0);
      var garmentAdjustment = Number(design.garmentAdjustment || 0);
      var locationAddon = Math.max(0, Number(design.locationAddon) || 0);
      var unitPrice = tier.price + garmentAdjustment + locationAddon;
      var subtotal = unitPrice * quantity;
      pricingSubtotal += subtotal;
      return {
        quantity: quantity,
        garmentAdjustment: garmentAdjustment,
        locationAddon: locationAddon,
        unitPrice: unitPrice,
        subtotal: subtotal
      };
    });
    var fulfillmentTotal = buyerPackages * buyerPackagePrice;
    var total = pricingSubtotal + fulfillmentTotal;

    return {
      available: true,
      requiresReview: false,
      totalQuantity: totalQuantity,
      tier: tier,
      nextTier: nextTier,
      reviewReasons: [],
      lines: lines,
      pricingSubtotal: pricingSubtotal,
      buyerPackages: buyerPackages,
      buyerPackagePrice: buyerPackagePrice,
      fulfillmentTotal: fulfillmentTotal,
      total: total,
      average: totalQuantity ? total / totalQuantity : 0
    };
  }

  function screenEstimate(config) {
    var quantity = Math.max(0, Number(config.quantity) || 0);
    var minimum = Number(config.minimum || 72);
    var setupPerScreen = Number(config.setupPerScreen || 35);
    var isDark = config.tone === "dark";
    var locations = config.locations || [];

    if (quantity < minimum) {
      return {
        available: false,
        reason: "Screen printing estimates begin at " + minimum + " pieces of one design. Digital print is usually the stronger value below that point, especially for multicolor artwork."
      };
    }

    var tier = getScreenBreak(quantity);
    var baseGarment = Number.isFinite(Number(config.baseGarment))
      ? Number(config.baseGarment)
      : SCREEN_BASE_GARMENT;
    var unitPrice = baseGarment;
    var totalScreens = 0;
    var lines = [];

    for (var index = 0; index < locations.length; index += 1) {
      var location = locations[index];
      var visibleColors = Math.max(1, Number(location.colors) || 1);
      var effectiveColors = visibleColors + (isDark ? 1 : 0);
      var rate = SCREEN_PUBLIC_LOCATION_RATES[tier] && SCREEN_PUBLIC_LOCATION_RATES[tier][effectiveColors];
      if (!rate) {
        return {
          available: false,
          reason: "This color and underbase combination needs a custom review. Send the artwork and we will confirm the best production approach."
        };
      }

      var placementAdder = SCREEN_PLACEMENT_ADDER[location.placement] || 0;
      var locationRate = rate + placementAdder;
      unitPrice += locationRate;
      totalScreens += effectiveColors;
      lines.push({
        placement: location.placement,
        visibleColors: visibleColors,
        effectiveColors: effectiveColors,
        hasUnderbase: isDark,
        placementAdder: placementAdder,
        locationRate: locationRate
      });
    }

    var shirtSubtotal = quantity * unitPrice;
    var setupTotal = totalScreens * setupPerScreen;
    var total = shirtSubtotal + setupTotal;

    return {
      available: true,
      quantity: quantity,
      tier: tier,
      baseGarment: baseGarment,
      unitPrice: unitPrice,
      totalScreens: totalScreens,
      lines: lines,
      shirtSubtotal: shirtSubtotal,
      setupTotal: setupTotal,
      total: total,
      average: quantity ? total / quantity : 0
    };
  }

  global.TaproomQuoteMath = {
    defaultDigitalTiers: DEFAULT_DIGITAL_TIERS.slice(),
    parseDigitalTiers: parseDigitalTiers,
    getDigitalTier: getDigitalTier,
    getNextDigitalTier: getNextDigitalTier,
    getScreenBreak: getScreenBreak,
    digitalEstimate: digitalEstimate,
    screenEstimate: screenEstimate
  };
})(typeof globalThis !== "undefined" ? globalThis : this);
